import { Box, LinearProgress, Typography } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '@fontsource/rubik';
import { display, formatDateTime, formatDay, percentUsed } from '../utils';
import { barTrackSx, BORDER, bodySx, captionSx, cardSx, DANGER, eyebrowSx, INK, MUTED, pillSx, SOFT, titleSx } from './styles';

// Storage arrives as a float; keep one decimal at most.
const amount = (value) => {
  if (value === null || value === undefined || value === '') return display(value);
  const n = Number(value);
  return Number.isInteger(n) ? n : Math.round(n * 10) / 10;
};

const barColor = (pct, alarm) => {
  if (alarm || pct >= 100) return DANGER;
  if (pct >= 80) return '#E6A100';
  return 'primary.main';
};

function UsageTile({ label, unit, used, total, side, note, alarm, testId }) {
  const pct = percentUsed(used, total);
  return (
    <Box
      data-testid={testId}
      sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', p: { xs: '14px', md: '16px 18px' }, minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography sx={eyebrowSx}>{label}</Typography>
        {pct !== null && <Typography sx={eyebrowSx}>{Math.round(pct)}%</Typography>}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <Typography
          sx={{ fontFamily: 'Rubik', fontWeight: 700, letterSpacing: '-0.02em', color: INK, fontSize: { xs: '26px', sm: '24px', lg: '28px', xl: '32px' }, lineHeight: 1.1 }}
        >
          {amount(used)}
          <Box component="span" sx={{ fontWeight: 500, color: MUTED, fontSize: '0.55em', letterSpacing: 0 }}>
            {' / '}{amount(total)}{unit ? ` ${unit}` : ''}
          </Box>
        </Typography>
        {side && <Typography sx={{ ...bodySx, color: MUTED }}>{side}</Typography>}
      </Box>

      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        aria-label={label}
        sx={{ ...barTrackSx, '& .MuiLinearProgress-bar': { borderRadius: '99px', backgroundColor: barColor(pct ?? 0, alarm) } }}
      />
      {note && <Typography sx={{ ...captionSx, mt: 1, color: alarm ? DANGER : MUTED }}>{note}</Typography>}
    </Box>
  );
}

function Fact({ icon: Icon, testId, children }) {
  return (
    <Box data-testid={testId} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: '8px', backgroundColor: SOFT, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon sx={{ fontSize: 17, color: INK }} />
      </Box>
      <Typography sx={bodySx}>{children}</Typography>
    </Box>
  );
}

export default function WalletSummary({ data, minutesPerCredit }) {
  const { organisation, deal, managed } = data || {};
  const shared = deal?.pools?.shared || {};
  const storage = deal?.storage || {};
  const unrestricted = shared.granted === null || shared.granted === undefined;

  const sharedNote = unrestricted
    ? 'No pool size has been set for this contract, so credits are not capped here.'
    : `Period ${formatDay(shared.periodStart)} to ${formatDay(shared.periodEnd)}`;

  return (
    <Box sx={cardSx} data-testid="wallet-summary">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...eyebrowSx, mb: 0.5 }}>Credit wallet</Typography>
          <Typography sx={titleSx}>{organisation?.name}</Typography>
        </Box>
        {managed && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Box component="span" sx={pillSx}>Deal v{display(deal?.version)} ({display(deal?.source)})</Box>
            <Box component="span" sx={{ ...pillSx, color: MUTED }}>Synced {formatDateTime(deal?.synced_at)}</Box>
            {deal?.allow_overage && (
              <Box component="span" sx={{ ...pillSx, borderColor: '#F3D58A', backgroundColor: '#FFF8E5' }}>Overage allowed</Box>
            )}
          </Box>
        )}
      </Box>

      {!managed && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, p: '10px 12px', borderRadius: '10px', backgroundColor: SOFT }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
          <Typography sx={{ ...bodySx, color: MUTED }}>Credits for this organisation are not managed in Superhub yet</Typography>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 2 }}>
        <UsageTile
          testId="shared-credits-bar"
          label="Shared credits"
          used={shared.used}
          total={shared.granted}
          side={unrestricted ? null : `${display(shared.remaining)} remaining`}
          note={sharedNote}
        />
        <UsageTile
          testId="storage-bar"
          label="Storage"
          unit="GB"
          used={storage.used_gb}
          total={storage.cap_gb}
          note={storage.reached ? 'Storage cap reached' : null}
          alarm={!!storage.reached}
        />
      </Box>

      {(shared.periodEnd || minutesPerCredit) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 4 }, mt: 2 }}>
          {shared.periodEnd && (
            <Fact icon={EventOutlinedIcon} testId="renews-on">
              Current period renews on <b>{formatDay(shared.periodEnd)}</b>
            </Fact>
          )}
          {minutesPerCredit ? (
            <Fact icon={ScheduleOutlinedIcon} testId="credit-rate">
              1 credit = up to {minutesPerCredit} minutes of edited video
            </Fact>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
